/**
 * The auth state holder (docs/specs/accounts.md, section 8). One module-level store, read through
 * `useAuth`. It is not React state, because api.ts must reach it from outside any component: a 401
 * from any request ends the login, whichever view made the request.
 *
 * The redirect rule lives here and only here. A 401 sends the person to `#/login` with the hash
 * they were on, and a login sends them back to it (`loginHashFor` and `safeReturnTo` in router.ts).
 */
import { useSyncExternalStore } from 'react';
import * as authApi from '../auth/authApi.ts';
import type { Me, Role } from '../auth/authApi.ts';
import { ApiError } from './http.ts';
import { isPublicRoute, loginHashFor, parseHash, replaceHash } from './router.ts';

export type AuthState =
  /** `me` has not answered yet. The app renders no shell in this state. */
  | { status: 'loading' }
  | { status: 'signed_out' }
  | { status: 'signed_in'; me: Me }
  /** `me` failed for a reason other than 401. Not the same claim as "signed out". */
  | { status: 'unavailable'; message: string };

const LOADING: AuthState = { status: 'loading' };
const SIGNED_OUT: AuthState = { status: 'signed_out' };

let state: AuthState = LOADING;
const listeners = new Set<() => void>();

function setState(next: AuthState): void {
  state = next;
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getAuthState(): AuthState {
  return state;
}

export function useAuth(): AuthState {
  return useSyncExternalStore(subscribe, getAuthState, () => LOADING);
}

/** Tests only: put the store back to its first state. */
export function resetAuthForTest(): void {
  state = LOADING;
  listeners.clear();
}

// ---- the 401 rule ----

/**
 * The login has ended. Called by api.ts for a 401 from any request, and by this module when `me`
 * answers 401. A public route keeps its hash: a person reading a setup link is not logged in, and
 * that is not a reason to move them. A second 401 that lands after the redirect finds the login
 * route and does nothing, so the first return hash survives.
 *
 * Load-bearing order: the state goes to signed out BEFORE the hash changes, and that update must
 * stay synchronous. `setState` calls its listeners in line, and `useSyncExternalStore` renders a
 * store change urgently, so the shell — and `ArtifactView` with it — is unmounted by the time the
 * browser delivers `hashchange` as a later task. If the update were deferred (a transition, a
 * timer, React state), `ArtifactView`'s `hashchange` listener would still be alive on the login
 * hash, read `?return=…` as filter state, and rewrite it through `setHashQuery`. The return route
 * would be lost.
 */
export function handleUnauthorized(): void {
  if (state.status !== 'signed_out') setState(SIGNED_OUT);
  const hash = window.location.hash;
  if (!isPublicRoute(parseHash(hash))) window.location.hash = loginHashFor(hash);
}

// ---- actions ----

const messageOf = (e: unknown): string => (e instanceof Error ? e.message : String(e));

/** Ask the server who this is. Called once on load, and again to refresh the runs-left count. */
export async function loadMe(): Promise<void> {
  try {
    setState({ status: 'signed_in', me: await authApi.me() });
  } catch (e) {
    if (e instanceof ApiError && e.status === 401) {
      handleUnauthorized();
      return;
    }
    // A refresh that fails keeps what we know. Only a first load has nothing to fall back on.
    if (state.status !== 'signed_in') setState({ status: 'unavailable', message: messageOf(e) });
  }
}

/** The credential was right and the cookie still did not work. Its message is whole: show it as is. */
export class LoginDidNotHoldError extends Error {
  constructor() {
    super(
      'The server accepted the password, but the login did not hold. Check that this browser allows cookies.',
    );
  }
}

/**
 * The server accepted the credential and set the cookie. From here on nothing may reject into the
 * form as if the credential had failed. `login` and `setup` answer with the account, so the normal
 * path awaits nothing. An unreadable answer falls back to `loadMe`, which never rejects. The one
 * throw left is a login that did not hold, and its message says that, not "wrong password".
 */
async function enter(account: Me | null, leave: () => void): Promise<void> {
  if (account) setState({ status: 'signed_in', me: account });
  else await loadMe();
  if (state.status === 'signed_out') {
    throw new LoginDidNotHoldError();
  }
  leave();
}

/**
 * Throws ApiError: 400 for a missing field, 401 for a wrong email or password, 429 when the email
 * is throttled. `returnTo` comes from the login route, already validated.
 */
export async function signIn(email: string, password: string, returnTo: string | null): Promise<void> {
  const account = await authApi.login(email, password);
  await enter(account, () => {
    window.location.hash = returnTo ?? '#/';
  });
}

/**
 * Throws ApiError 400 for a dead link or a refused password. It leaves by `replaceHash`: the hash
 * being left holds the setup token, and it must not stay one Back press away.
 */
export async function completeSetup(token: string, password: string): Promise<void> {
  const account = await authApi.setup(token, password);
  await enter(account, () => replaceHash('#/'));
}

/**
 * Throws when the server did not confirm the logout. The store then stays signed in, because the
 * cookie is still good, and saying otherwise on a shared machine would be a lie.
 */
export async function signOut(): Promise<void> {
  try {
    await authApi.logout();
  } catch (e) {
    // 401: the login was already gone, which is the outcome the person asked for.
    if (!(e instanceof ApiError && e.status === 401)) throw e;
  }
  setState(SIGNED_OUT);
  window.location.hash = '#/login';
}

/**
 * Throws ApiError 400 when the current password is wrong or the new one is refused. A 401 here
 * means only that the login is gone, so it takes the same road as a 401 from any other request.
 */
export async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  try {
    await authApi.changePassword(currentPassword, newPassword);
  } catch (e) {
    if (e instanceof ApiError && e.status === 401) handleUnauthorized();
    throw e;
  }
}

// ---- what the role and the quota mean to a view ----

/** Spec section 5: a non-admin never sees a dollar figure. */
export function canSeeCost(role: Role): boolean {
  return role === 'admin';
}

/**
 * The header's quota line, or null for an account with no quota (spec section 5). Never negative:
 * an admin can lower a quota below what the person already used, and "-2 runs left" is not a count.
 */
export function quotaLine(me: Pick<Me, 'monthly_run_quota' | 'runs_used_this_month'>): string | null {
  if (me.monthly_run_quota == null) return null;
  const left = Math.max(0, me.monthly_run_quota - me.runs_used_this_month);
  return `${left} of ${me.monthly_run_quota} runs left this month`;
}
